import { NotificationList } from 'components/common/NotificationList'
import { put, takeLatest } from 'redux-saga/effects'
import { makeGetRequest, makePutRequest, makePostRequest, makeDeleteRequest } from 'utils/api'
import { formatResponse, formatErrorMessage } from 'utils/formatting'
import {
  toProposalUpdateSerializer,
  toProposalFormData,
  toProposalSerializer,
  toProposalEmail,
  toProposalHistoryContacts,
  toProposalReviewersSerializer
} from './serializers'
import { fromBoardItems, toTask, toSingleTask } from 'utils/serializers'
import { toBoardData } from 'components/proposals/Proposals/serializers'

import * as ACT from './actions'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import { refreshNotifications } from 'utils/helpers'

function* fetchProposal(action) {
  try {
    const { id } = action.payload
    const url = `/v1/proposals/${id}`
    const proposal = yield makeGetRequest(url)
    yield put({
      type: ACT.PROPOSAL_FETCH_SUCCESS,
      payload: {
        proposal: toProposalSerializer(proposal)
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching the Proposal',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* createProposal(action) {
  try {
    const { proposal, addProposalActionType, filters, userId, fromDashboard, createProposalCb } =
      action.payload
    const { deal, file } = proposal
    const { value: dealId } = deal
    const url = '/v1/proposals'
    const response = yield makePostRequest(url, toProposalFormData(proposal, dealId, file))

    // this is done to refresh the list view for deal proposals tab
    !fromDashboard && createProposalCb?.()

    yield put({
      type: ACT.PROPOSAL_CREATE_SUCCESS,
      payload: {
        proposal: response,
        fromDashboard
      }
    })

    if (fromDashboard) {
      yield put({
        type: ACT.DASHBOARD_UPDATE_NEW_PROPOSALS_COUNTER
      })
    }

    if (addProposalActionType) {
      yield put({
        type: addProposalActionType,
        payload: {
          proposal: response,
          dealId,
          filters,
          userId
        }
      })
    }

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Proposal created successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue creating the Proposal',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* updateProposal(action) {
  const { proposal } = action.payload
  try {
    const url = `/v1/proposals/noattachment/${proposal.id}`
    const response = yield makePutRequest(url, toProposalUpdateSerializer(proposal))

    yield put({
      type: ACT.PROPOSAL_UPDATE_SUCCESS,
      payload: {
        proposal: response
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Proposal updated successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Proposal',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* approveProposal(action) {
  const { id } = action.payload
  try {
    const url = `/v1/proposals/${id}/approve`
    const response = yield makePutRequest(url)

    yield put({
      type: ACT.PROPOSAL_APPROVE_SUCCESS,
      payload: {
        proposal: response
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Proposal approved successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue approving Proposal',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* deleteProposal(action) {
  const { id, navigate, dealId } = action.payload
  try {
    const url = `/v1/proposals/${id}`
    yield makeDeleteRequest(url)
    yield put({
      type: ACT.PROPOSAL_DELETE_SUCCESS
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Proposal deleted successfully',
        level: 'success'
      }
    })
    navigate(`/opportunities/${dealId}/proposals`)
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue deleting Proposal',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* updateProposalFile(action) {
  const { file, proposal, dealId } = action.payload
  try {
    const url = `/v1/proposals/${proposal.id}`
    const response = yield makePutRequest(url, toProposalFormData(proposal, dealId, file))

    yield put({ type: ACT.PROPOSAL_UPDATE_FILE_SUCCESS })
    yield put({
      type: ACT.PROPOSAL_FETCH_SUCCESS,
      payload: {
        proposal: toProposalSerializer(response)
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Proposal file uploaded successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue repacing Proposal file',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* updateProposalStatus(action) {
  const { id, status, oldStatus, filters, pagination } = action.payload
  const { endDate, startDate } = filters
  try {
    const url = `/v1/proposals/board/${id}/status/${status}/${startDate}/${endDate}`
    const response = yield makePutRequest(url)
    const rowData = fromBoardItems(response)

    yield put({
      type: ACT.PROPOSAL_UPDATE_STATUS_SUCCESS,
      payload: {
        proposals: rowData,
        paginatedProposals: toBoardData(rowData),
        oldStatus,
        id,
        status,
        pagination
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Proposal updated successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Proposal',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchProposalTasks(action) {
  try {
    const { id, name } = action.payload
    const url = `/v1/proposals/${id}/tasks`
    const response = yield makeGetRequest(url)

    yield put({
      type: ACT.PROPOSAL_TASKS_FETCH_SUCCESS,
      payload: {
        tasks: response,
        proposalName: name
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching the Proposal tasks',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* markTaskCompleted(action) {
  try {
    const { id } = action.payload
    const url = `/v1/tasks/${id}/markcompleted`
    const task = yield makePutRequest(url)

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Task was marked as completed!',
        level: 'success'
      }
    })
    yield put({
      type: ACT.PROPOSAL_TASK_MARK_COMPLETED_SUCCESS,
      payload: {
        task
      }
    })

    yield refreshNotifications(put)
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue marking the Task as completed',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* markTaskNotCompleted(action) {
  try {
    const { id } = action.payload
    const url = `/v1/tasks/${id}/marknotcompleted`
    const task = yield makePutRequest(url)

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Task was marked as not completed!',
        level: 'success'
      }
    })
    yield put({
      type: ACT.PROPOSAL_TASK_MARK_COMPLETED_SUCCESS,
      payload: {
        task
      }
    })
    yield put({
      type: ACT.PROPOSAL_TASK_MARK_NOT_COMPLETED_SUCCESS
    })

    yield refreshNotifications(put)
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue marking the Task as not completed',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* updateTask(action) {
  const { task } = action.payload
  try {
    const url = `/v1/tasks/${task.id}`
    const taskObj = toTask(task, {}, true)
    const response = yield makePutRequest(url, taskObj)

    yield put({
      type: ACT.UPDATE_PROPOSAL_TASK_SUCCESS,
      payload: {
        task: response
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Task updated successfully',
        level: 'success'
      }
    })

    yield refreshNotifications(put)
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Task',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* deleteTask(action) {
  const { task } = action.payload
  try {
    const url = `/v1/tasks/${task.id}`
    yield makeDeleteRequest(url)

    yield put({
      type: ACT.DELETE_PROPOSAL_TASK_SUCCESS,
      payload: {
        task
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Task deleted successfully',
        level: 'success'
      }
    })

    yield refreshNotifications(put)
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue deleting Task',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* createTask(action) {
  const { proposalId, task } = action.payload
  try {
    const url = '/v1/tasks'
    const taskObj = toSingleTask(task)
    taskObj.proposalIds = [proposalId]
    const response = yield makePostRequest(url, taskObj)

    yield put({
      type: ACT.CREATE_PROPOSAL_TASK_SUCCESS,
      payload: {
        task: response
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Task created successfully',
        level: 'success'
      }
    })
    yield refreshNotifications(put)
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue creating Task for Proposal',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* updateHtmlContent(action) {
  const { proposalId, htmlContent, templateId, content, pageType } = action.payload
  try {
    const updateProposalTemplateUrl = `/v1/proposaltemplates/${templateId}`

    // saves word editor sfdt format
    const { id } = yield makePutRequest(updateProposalTemplateUrl, {
      content: content,
      ...(pageType ? { pageType: pageType.toUpperCase() } : {})
    })

    const url = `/v1/proposals/${proposalId}/html`

    const response = yield makePutRequest(url, {
      value: htmlContent,
      proposalTemplateId: id,
      ...(pageType ? { pageType: pageType.toUpperCase() } : {})
    })

    yield put({
      type: ACT.UPDATE_PROPOSAL_HTML_SUCCESS,
      payload: {
        proposal: response
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Proposal content updated successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Proposal content',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* emailProposal(action) {
  const { id, toContacts, ccContacts, bccContacts, subject, emailBody, files, splitMode } =
    action.payload
  try {
    const url = `/v1/proposals/${id}/sendEmail`
    const emailData = toProposalEmail(
      toContacts,
      ccContacts,
      bccContacts,
      subject,
      emailBody,
      files,
      splitMode
    )
    const response = yield makePostRequest(url, emailData)

    yield put({
      type: ACT.SHOW_PROPOSAL_FOLLOWUP,
      payload: {
        showProposalFollowup: true
      }
    })

    yield put({
      type: ACT.EMAIL_PROPOSAL_TO_CLIENT_SUCCESS,
      payload: {
        proposal: response
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Proposal email sent successfully',
        level: 'success'
      }
    })
    yield fetchProposalContactHistory({
      payload: {
        id
      }
    })
    yield refreshNotifications(put)
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue emailing the Proposal',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchProposalContactHistory(action) {
  const { id } = action.payload
  try {
    const url = `/v1/proposals/${id}/contacts/sentHistory`
    const response = yield makeGetRequest(url)

    yield put({
      type: ACT.PROPOSAL_CONTACTS_HISTOY_FETCH_SUCCESS,
      payload: {
        contacts: toProposalHistoryContacts(response)
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching the Proposal Contact History',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* sendProposalForReview(action) {
  const { proposalId, users } = action.payload
  try {
    const url = `/v1/proposals/${proposalId}/sendForReview`
    const response = yield makePutRequest(url, toProposalReviewersSerializer(users))

    yield put({
      type: ACT.PROPOSAL_SEND_FOR_REVIEW_SUCCESS,
      payload: {
        proposal: response
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Proposal updated successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Proposal',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

const proposalSagas = [
  takeLatest(ACT.PROPOSAL_FETCH_REQUESTED, fetchProposal),
  takeLatest(ACT.PROPOSAL_CREATE_REQUESTED, createProposal),
  takeLatest(ACT.PROPOSAL_UPDATE_REQUESTED, updateProposal),
  takeLatest(ACT.PROPOSAL_APPROVE_REQUESTED, approveProposal),
  takeLatest(ACT.PROPOSAL_DELETE_REQUESTED, deleteProposal),
  takeLatest(ACT.PROPOSAL_UPDATE_FILE_REQUESTED, updateProposalFile),
  takeLatest(ACT.PROPOSAL_UPDATE_STATUS_REQUESTED, updateProposalStatus),
  takeLatest(ACT.PROPOSAL_TASKS_FETCH_REQUESTED, fetchProposalTasks),
  takeLatest(ACT.PROPOSAL_TASK_MARK_COMPLETED_REQUESTED, markTaskCompleted),
  takeLatest(ACT.PROPOSAL_TASK_MARK_NOT_COMPLETED_REQUESTED, markTaskNotCompleted),
  takeLatest(ACT.UPDATE_PROPOSAL_TASK_REQUESTED, updateTask),
  takeLatest(ACT.CREATE_PROPOSAL_TASK_REQUESTED, createTask),
  takeLatest(ACT.UPDATE_PROPOSAL_HTML_REQUESTED, updateHtmlContent),
  takeLatest(ACT.EMAIL_PROPOSAL_TO_CLIENT_REQUESTED, emailProposal),
  takeLatest(ACT.DELETE_PROPOSAL_TASK_REQUESTED, deleteTask),
  takeLatest(ACT.PROPOSAL_CONTACTS_HISTOY_FETCH_REQUESTED, fetchProposalContactHistory),
  takeLatest(ACT.PROPOSAL_SEND_FOR_REVIEW_REQUESTED, sendProposalForReview)
]

export default proposalSagas
